import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings, LayoutDashboard, Loader2, Save } from "lucide-react";

export default function SystemSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => toast.error("Failed to load settings")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", form);
      setForm(res.data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950 flex items-center gap-2"><Settings className="h-7 w-7 text-emerald-700" /> System Settings</h1>
            <p className="mt-1.5 text-slate-500">Organisation profile and global application controls.</p>
          </div>
          <Button variant="ghost" className="rounded-full text-emerald-700 h-10" onClick={() => navigate("/admin")} data-testid="settings-back-console"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Console</Button>
        </div>

        {loading || !form ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
        ) : (
          <Card className="mt-6 rounded-3xl border-emerald-100 p-6 sm:p-8 space-y-5" data-testid="settings-form">
            <Field label="Organisation Name"><Input value={form.org_name || ""} onChange={(e) => set("org_name", e.target.value)} className="h-11 rounded-xl" data-testid="settings-org-name" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Support Email"><Input type="email" value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} className="h-11 rounded-xl" data-testid="settings-support-email" /></Field>
              <Field label="Support Phone"><Input value={form.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} className="h-11 rounded-xl" data-testid="settings-support-phone" /></Field>
            </div>
            <Field label="Office Address"><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="h-11 rounded-xl" data-testid="settings-address" /></Field>
            <div className="flex items-center justify-between rounded-2xl bg-[#f1f6f2] p-4">
              <div>
                <div className="font-head font-semibold text-emerald-950 text-sm">Accepting new applications</div>
                <div className="text-xs text-slate-500 mt-0.5">When off, farmers and staff cannot submit new applications.</div>
              </div>
              <Switch checked={!!form.accepting_applications} onCheckedChange={(v) => set("accepting_applications", v)} data-testid="settings-accepting-toggle" />
            </div>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-emerald-800 text-white h-11 px-6" data-testid="settings-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Settings</>}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-slate-600 text-xs">{label}</Label><div className="mt-1.5">{children}</div></div>
);
